/**
 * API Constants
 * Centralized API route definitions and configuration constants
 */

/**
 * API route prefixes
 */
export const API_ROUTES = {
  // Base routes
  HEALTH: '/health',
  CATEGORIES: '/categories',
  VENDORS: '/vendors',
  CUSTOMERS: '/customers',
  LOCATIONS: '/locations',
  BANKS: '/banks',
  TAX_TYPES: '/tax-types',
  PAYMENT_TERMS: '/payment-terms',
  AUTH: '/auth',
  GOVERNANCE: '/governance',
  CLAIMS: '/claims',
  // Wave 1–3 mock domains
  PRODUCTS: '/products',
  PRODUCT_TAXONOMY: '/product-taxonomy',
  /** SCN 共同規格主檔 Spec Master (S7.1) — read-only, still mock-side (§0g). */
  SCN_GROUPS: '/scn-groups',
  SALES_ORDERS: '/sales-orders',
  PURCHASE_ORDERS: '/purchase-orders',
  OCC_BOARD: '/occ/board',
  SHIPMENT_BATCHES: '/shipment-batches',
  SHIPPING_NOTICES: '/shipping-notices',
  GOODS_RECEIPTS: '/goods-receipts',
  FINANCE_AP: '/finance/ap-invoices',
  FINANCE_AR: '/finance/ar-invoices',
  FINANCE_GL: '/finance/gl-statements',
  ENGINES_PRICING: '/engines/pricing',
  ENGINES_COSTING: '/engines/costing',
  REPORTS_OPS: '/reports/ops-pack',
} as const;

/**
 * Vendor-specific routes (use with vendorId parameter)
 */
export const VENDOR_ROUTES = {
  CONTACTS: (vendorId: number) => `${API_ROUTES.VENDORS}/${vendorId}/contacts`,
  PAYMENT: (vendorId: number) => `${API_ROUTES.VENDORS}/${vendorId}/payment`,
  BANKS: (vendorId: number) => `${API_ROUTES.VENDORS}/${vendorId}/banks`,
  TRANSACTIONS: (vendorId: number) => `${API_ROUTES.VENDORS}/${vendorId}/transactions`,
} as const;

/**
 * Customer-specific routes (use with customerId parameter)
 */
export const CUSTOMER_ROUTES = {
  CONTACTS: (customerId: number) => `${API_ROUTES.CUSTOMERS}/${customerId}/contacts`,
  LOCATIONS: (customerId: number) => `${API_ROUTES.CUSTOMERS}/${customerId}/locations`,
  PAYMENT: (customerId: number) => `${API_ROUTES.CUSTOMERS}/${customerId}/payment`,
  TRANSACTIONS: (customerId: number) => `${API_ROUTES.CUSTOMERS}/${customerId}/transactions`,
} as const;

/**
 * Location-specific routes
 */
export const LOCATION_ROUTES = {
  ALL: `${API_ROUTES.LOCATIONS}/all`,
  COUNTRIES: `${API_ROUTES.LOCATIONS}/countries`,
  STATES_BY_COUNTRY: (countryCode: string) =>
    `${API_ROUTES.LOCATIONS}/countries/${countryCode}/states`,
  CITIES_BY_STATE: (stateId: number) => `${API_ROUTES.LOCATIONS}/states/${stateId}/cities`,
  LOOKUP_BY_POSTCODE: (countryCode: string, postcode: string) =>
    `${API_ROUTES.LOCATIONS}/postcode/${countryCode}/${postcode}`,
} as const;

/**
 * Bank-specific routes
 */
export const BANK_ROUTES = {
  SEARCH: `${API_ROUTES.BANKS}`,
  BRANCHES: `${API_ROUTES.BANKS}/branches`,
} as const;

/**
 * Default pagination settings
 */
export const DEFAULT_PAGINATION = {
  PAGE: 1,
  PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
} as const;

/**
 * API response status codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * Request timeout in milliseconds
 */
export const REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Cache time-to-live settings (in milliseconds)
 */
export const CACHE_TTL = {
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 15 * 60 * 1000, // 15 minutes
  LONG: 60 * 60 * 1000, // 1 hour
  PERMANENT: 24 * 60 * 60 * 1000, // 24 hours
} as const;

/**
 * ahooks cache configuration presets
 */
export const CACHE_CONFIG = {
  // For frequently changing data (e.g., vendor list with filters)
  DYNAMIC: {
    cacheTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 60000, // 1 minute
  },
  // For relatively stable data (e.g., vendor details)
  STABLE: {
    cacheTime: 15 * 60 * 1000, // 15 minutes
    staleTime: 30000, // 30 seconds
  },
  // For reference data that rarely changes (e.g., countries, categories)
  STATIC: {
    cacheTime: 60 * 60 * 1000, // 1 hour
    staleTime: 5 * 60 * 1000, // 5 minutes
  },
} as const;
